import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Car, Bike, MapPin, Crosshair, ArrowRight, AlertCircle, Banknote, Smartphone, Lock, Loader2, Tag, Check } from "lucide-react";
import { useAuth } from "../../lib/auth";
import {
  estimate, initiatePayment, confirmSandboxOTP, applyPromoCode, formatFcfa, formatDuration,
  VEHICLES, NDJAMENA_CENTER,
} from "../../lib/ride";
import PaymentSuccessOverlay from "../../components/PaymentSuccessOverlay";
import { toast } from "sonner";

const pickupIcon = new L.DivIcon({
  className: "ride-pin",
  html: `<div style="background:#C8A04D;color:#0F0F0F;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)"><span style="transform:rotate(45deg);font-weight:700;font-size:13px">A</span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 30],
});
const destIcon = new L.DivIcon({
  className: "ride-pin",
  html: `<div style="background:#B25E3F;color:#fff;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)"><span style="transform:rotate(45deg);font-weight:700;font-size:13px">B</span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 30],
});

function ClickPicker({ pickingMode, onPick }) {
  useMapEvents({
    click(e) {
      if (!pickingMode) return;
      onPick(pickingMode, { lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

export default function RideRequestPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initialType = params.get("type") === "moto" ? "moto" : "taxi";
  const [vehicleType, setVehicleType] = useState(initialType);
  const [pickup, setPickup] = useState(null);  // {lat,lng,address}
  const [destination, setDestination] = useState(null);
  const [pickupAddr, setPickupAddr] = useState("");
  const [destAddr, setDestAddr] = useState("");
  const [pickingMode, setPickingMode] = useState("pickup"); // 'pickup' | 'dest' | null
  const [est, setEst] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash"); // cash | airtel | moov
  const [msisdn, setMsisdn] = useState("");
  const [stkPayment, setStkPayment] = useState(null); // {payment_id, sandbox_otp_hint, ...}
  const [otpInput, setOtpInput] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_xaf, final_amount_xaf, message}
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null); // {provider, amount, ref, rideId}
  const mapRef = useRef(null);

  // Try GPS for pickup on first load
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        // Only auto-set if inside N'Djamena bounding box (rough check)
        if (Math.abs(here.lat - NDJAMENA_CENTER.lat) < 0.3 && Math.abs(here.lng - NDJAMENA_CENTER.lng) < 0.3) {
          setPickup({ ...here });
          setPickupAddr("Ma position actuelle");
          setPickingMode("dest");
          mapRef.current?.setView([here.lat, here.lng], 14);
        }
      },
      () => {},
      { timeout: 4000, enableHighAccuracy: false }
    );
  }, []);

  const handlePick = (mode, latlng) => {
    if (mode === "pickup") {
      setPickup(latlng);
      setPickupAddr(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
      setPickingMode("dest");
    } else if (mode === "dest") {
      setDestination(latlng);
      setDestAddr(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
      setPickingMode(null);
    }
  };

  // Re-estimate when both points and type are set
  useEffect(() => {
    if (!pickup || !destination) {
      setEst(null);
      return;
    }
    setEstimating(true);
    setError("");
    // Any promo previously applied must be revalidated against the new amount
    setPromoApplied(null);
    setPromoError("");
    estimate({
      pickup: { ...pickup, address: pickupAddr },
      destination: { ...destination, address: destAddr },
      vehicle_type: vehicleType,
    })
      .then(setEst)
      .catch((e) => {
        setError(e.response?.data?.detail || "Impossible d'estimer la course");
        setEst(null);
      })
      .finally(() => setEstimating(false));
  }, [pickup, destination, vehicleType, pickupAddr, destAddr]);

  const tryApplyPromo = async () => {
    if (!promoCode.trim() || !est) return;
    setPromoChecking(true);
    setPromoError("");
    try {
      const res = await applyPromoCode(promoCode, est.estimated_price);
      setPromoApplied(res);
      toast.success(`Code « ${res.code} » appliqué`);
    } catch (e) {
      setPromoApplied(null);
      setPromoError(e.response?.data?.detail || "Code invalide");
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
    setPromoError("");
  };

  const finalPrice = promoApplied ? promoApplied.final_amount_xaf : est?.estimated_price;

  const submit = async () => {
    if (!user || user === false) {
      navigate("/login?next=/ride/request");
      return;
    }
    if (!pickup || !destination) {
      toast.error("Sélectionnez le départ et la destination");
      return;
    }
    if (paymentMethod !== "cash" && !/^\+?[\d\s]{8,}$/.test(msisdn.trim())) {
      toast.error("Saisissez un numéro de téléphone Airtel/Moov valide");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await initiatePayment({
        provider: paymentMethod,
        msisdn: paymentMethod === "cash" ? undefined : msisdn.trim(),
        pickup: { ...pickup, address: pickupAddr },
        destination: { ...destination, address: destAddr },
        vehicle_type: vehicleType,
        promo_code: promoApplied ? promoApplied.code : undefined,
      });
      if (res.status === "ride_created") {
        toast.success("Course créée — en attente d'un chauffeur");
        navigate(`/ride/track/${res.ride_id}`);
      } else if (res.status === "stk_push_sent") {
        toast.message("Demande de paiement envoyée à votre téléphone");
        setStkPayment(res);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmOtp = async () => {
    if (!stkPayment) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await confirmSandboxOTP(stkPayment.payment_id, otpInput);
      // Trigger the premium "Payment Success" overlay before navigating
      setPaymentSuccess({
        provider: stkPayment.provider,
        amount: stkPayment.amount_xaf,
        reference: stkPayment.reference,
        rideId: res.ride_id,
      });
      setStkPayment(null);
      setOtpInput("");
    } catch (e) {
      setError(e.response?.data?.detail || "Code OTP invalide");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-6" data-testid="ride-request">
      <Link to="/ride" className="text-xs text-brand-inkSoft hover:text-brand-ink">← Retour Kanama Ride</Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 mt-2">
        {/* Map */}
        <Card className="overflow-hidden rounded-2xl border-brand-line h-[60vh] lg:h-[78vh] relative" data-testid="ride-map">
          <MapContainer
            center={[NDJAMENA_CENTER.lat, NDJAMENA_CENTER.lng]}
            zoom={13}
            style={{ width: "100%", height: "100%" }}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickPicker pickingMode={pickingMode} onPick={handlePick} />
            {pickup && <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon} />}
            {destination && <Marker position={[destination.lat, destination.lng]} icon={destIcon} />}
          </MapContainer>

          <div className="absolute top-3 left-3 right-3 bg-white/95 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-md border border-brand-line text-sm">
            <div className="flex items-center gap-2 text-brand-ink">
              <Crosshair className="w-4 h-4 text-brand-terracotta" />
              {pickingMode === "pickup" ? <span className="font-semibold">Touchez la carte pour définir le départ (A)</span>
                : pickingMode === "dest" ? <span className="font-semibold">Touchez la carte pour définir la destination (B)</span>
                : <span className="text-brand-inkSoft">Itinéraire complet — modifiez les points si besoin</span>}
            </div>
          </div>
        </Card>

        {/* Form */}
        <div className="space-y-3">
          <Card className="rounded-2xl border-brand-line p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold mb-2">Type de véhicule</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {Object.values(VEHICLES).map((v) => {
                const active = vehicleType === v.key;
                const Icon = v.key === "taxi" ? Car : Bike;
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setVehicleType(v.key)}
                    data-testid={`ride-vehicle-${v.key}`}
                    className={`p-3 rounded-xl border-2 text-left transition-all min-h-[64px] ${
                      active ? "border-brand-gold bg-brand-gold/10" : "border-brand-line hover:border-brand-inkMuted"
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-1" style={{ color: v.color }} />
                    <div className="font-semibold text-sm text-brand-ink">{v.label}</div>
                    <div className="text-[11px] text-brand-inkMuted leading-tight">{v.description}</div>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold flex items-center gap-1.5 mb-1">
                  <span className="w-2 h-2 rounded-full bg-brand-gold" /> Départ (A)
                </label>
                <Input
                  value={pickupAddr}
                  onChange={(e) => setPickupAddr(e.target.value)}
                  onFocus={() => setPickingMode("pickup")}
                  placeholder="Touchez la carte pour choisir le départ"
                  className="h-12 text-base rounded-xl border-brand-line"
                  data-testid="ride-input-pickup"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold flex items-center gap-1.5 mb-1">
                  <span className="w-2 h-2 rounded-full bg-brand-terracotta" /> Destination (B)
                </label>
                <Input
                  value={destAddr}
                  onChange={(e) => setDestAddr(e.target.value)}
                  onFocus={() => setPickingMode("dest")}
                  placeholder="Touchez la carte pour choisir la destination"
                  className="h-12 text-base rounded-xl border-brand-line"
                  data-testid="ride-input-dest"
                />
              </div>
            </div>
          </Card>

          {/* Estimation card */}
          {est && !estimating && !error && (
            <Card className="rounded-2xl border-brand-gold bg-brand-gold/5 p-4" data-testid="ride-estimate">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-goldDark font-bold mb-1">Estimation</div>
              <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                {promoApplied ? (
                  <>
                    <span className="font-heading text-3xl font-bold text-brand-ink">{formatFcfa(promoApplied.final_amount_xaf)}</span>
                    <span className="text-sm text-brand-inkMuted line-through" data-testid="ride-base-price-strike">{formatFcfa(est.estimated_price)}</span>
                  </>
                ) : (
                  <span className="font-heading text-3xl font-bold text-brand-ink">{formatFcfa(est.estimated_price)}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-brand-inkMuted">Distance</span><div className="font-semibold text-brand-ink">{est.distance_km} km</div></div>
                <div><span className="text-brand-inkMuted">Durée estimée</span><div className="font-semibold text-brand-ink">{formatDuration(est.duration_min)}</div></div>
              </div>
              {promoApplied && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-2 text-xs text-emerald-800" data-testid="ride-promo-applied">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <strong>−{formatFcfa(promoApplied.discount_xaf)}</strong> · {promoApplied.message}
                  </div>
                  <button onClick={removePromo} className="text-emerald-700 hover:text-emerald-900 underline text-xs" data-testid="ride-promo-remove">Retirer</button>
                </div>
              )}
              <div className="mt-3 text-xs text-brand-inkMuted">
                Paiement {paymentMethod === "cash" ? "en espèces à l'arrivée" : "à régler avant l'envoi de la course"}
              </div>
            </Card>
          )}

          {/* Promo code input */}
          {est && !estimating && !error && (
            <Card className="rounded-2xl border-brand-line p-4" data-testid="ride-promo-card">
              <label className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold flex items-center gap-1.5 mb-2">
                <Tag className="w-3.5 h-3.5 text-brand-gold" /> Code promo
              </label>
              {promoApplied ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-brand-gold/10 border border-brand-gold/40" data-testid="ride-promo-locked">
                  <span className="font-mono font-bold text-sm tracking-wider text-brand-ink">{promoApplied.code}</span>
                  <span className="text-xs text-brand-goldDark font-semibold">−{formatFcfa(promoApplied.discount_xaf)}</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); tryApplyPromo(); } }}
                    placeholder="BIENVENUE, KANAMA1…"
                    autoCapitalize="characters"
                    autoComplete="off"
                    spellCheck="false"
                    className="flex-1 h-11 px-3 rounded-xl border border-brand-line text-base font-mono tracking-wider bg-white uppercase"
                    data-testid="ride-promo-input"
                  />
                  <Button
                    onClick={tryApplyPromo}
                    disabled={!promoCode.trim() || promoChecking}
                    className="h-11 px-5 rounded-xl bg-brand-ink hover:bg-brand-inkSoft text-white font-semibold"
                    data-testid="ride-promo-apply"
                  >
                    {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
                  </Button>
                </div>
              )}
              {promoError && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-red-700" data-testid="ride-promo-error">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{promoError}</span>
                </div>
              )}
            </Card>
          )}

          {estimating && <div className="text-center text-sm text-brand-inkSoft py-2">Calcul en cours…</div>}

          {/* Payment method picker (only enabled once estimate is ready) */}
          {est && !estimating && (
            <Card className="rounded-2xl border-brand-line p-4" data-testid="ride-payment-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold mb-2">Mode de paiement</div>
              <div className="space-y-2">
                {[
                  { k: "cash", Icon: Banknote, label: "Espèces", desc: "À régler au chauffeur à l'arrivée", color: "#2D5A3D" },
                  { k: "airtel", Icon: Smartphone, label: "Airtel Money", desc: "Paiement avant la course (STK push)", color: "#E60000" },
                  { k: "moov", Icon: Smartphone, label: "Moov Money", desc: "Paiement avant la course (STK push)", color: "#003DA5" },
                ].map(({ k, Icon, label, desc, color }) => {
                  const active = paymentMethod === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setPaymentMethod(k)}
                      data-testid={`ride-pay-${k}`}
                      className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all min-h-[60px] ${
                        active ? "border-brand-gold bg-brand-gold/10" : "border-brand-line"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}18`, color }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-brand-ink">{label}</div>
                        <div className="text-[11px] text-brand-inkMuted leading-tight">{desc}</div>
                      </div>
                      {active && <div className="w-4 h-4 rounded-full bg-brand-gold mt-1.5 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {paymentMethod !== "cash" && (
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold">Numéro {paymentMethod === "airtel" ? "Airtel" : "Moov"}</label>
                  <input
                    value={msisdn}
                    onChange={(e) => setMsisdn(e.target.value)}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+235 6X XX XX XX"
                    className="mt-1 w-full h-12 px-3 rounded-xl border border-brand-line text-base bg-white"
                    data-testid="ride-pay-msisdn"
                  />
                  <div className="mt-2 flex items-start gap-1.5 text-[11px] text-brand-inkSoft bg-brand-sandlight rounded-lg px-2.5 py-2">
                    <Lock className="w-3 h-3 mt-0.5 text-brand-gold shrink-0" />
                    <span>
                      <strong>Paiement avant réservation :</strong> votre course sera envoyée aux chauffeurs uniquement après confirmation du paiement.
                    </span>
                  </div>
                </div>
              )}
            </Card>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2" data-testid="ride-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            disabled={!est || submitting || !!error || (paymentMethod !== "cash" && !msisdn.trim())}
            onClick={submit}
            className="w-full h-14 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold text-base shadow-lg"
            data-testid="ride-submit"
          >
            {submitting
              ? "Traitement…"
              : paymentMethod === "cash"
              ? `Commander · ${formatFcfa(finalPrice ?? 0)}`
              : `Payer ${formatFcfa(finalPrice ?? 0)} & réserver`}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <p className="text-[11px] text-brand-inkMuted text-center px-2">
            <MapPin className="w-3 h-3 inline -mt-0.5 mr-0.5" />
            Phase 1 · service disponible uniquement dans la zone de N'Djamena
          </p>
        </div>
      </div>

      {/* STK push confirmation modal (sandbox mode shows OTP hint) */}
      {stkPayment && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 bg-black/50" data-testid="ride-stk-modal">
          <Card className="w-full max-w-md rounded-3xl bg-white p-5">
            <div className="text-center mb-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-brand-gold/15 flex items-center justify-center text-brand-goldDark mb-3">
                <Smartphone className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-brand-ink">Confirmation paiement</h3>
              <p className="text-sm text-brand-inkSoft mt-1">
                Une demande de <strong className="text-brand-ink">{formatFcfa(stkPayment.amount_xaf)}</strong>{" "}
                a été envoyée au {stkPayment.msisdn} via {stkPayment.provider === "airtel" ? "Airtel Money" : "Moov Money"}.
              </p>
            </div>
            {stkPayment.environment_mode === "sandbox" && stkPayment.sandbox_otp_hint && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-3" data-testid="ride-stk-sandbox-hint">
                <strong>Mode SANDBOX :</strong> tapez le code <span className="font-mono font-bold tracking-widest text-base">{stkPayment.sandbox_otp_hint}</span> pour simuler la confirmation.
              </div>
            )}
            <label className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold">Code de confirmation (OTP)</label>
            <input
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="••••"
              className="mt-1 w-full h-14 px-3 rounded-xl border-2 border-brand-line text-2xl tracking-[0.4em] text-center font-mono bg-white"
              data-testid="ride-stk-otp"
            />
            {error && (
              <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button variant="outline" onClick={() => { setStkPayment(null); setOtpInput(""); setError(""); }} className="rounded-full" data-testid="ride-stk-cancel">
                Annuler
              </Button>
              <Button onClick={confirmOtp} disabled={otpInput.length < 4 || submitting} className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold" data-testid="ride-stk-confirm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
              </Button>
            </div>
            <p className="text-[10px] text-brand-inkMuted text-center mt-3">
              La course sera envoyée aux chauffeurs uniquement après confirmation du paiement.
            </p>
          </Card>
        </div>
      )}

      {/* Premium payment success celebration (digital only) */}
      {paymentSuccess && (
        <PaymentSuccessOverlay
          provider={paymentSuccess.provider}
          amountLabel={formatFcfa(paymentSuccess.amount)}
          reference={paymentSuccess.reference}
          onDone={() => navigate(`/ride/track/${paymentSuccess.rideId}`)}
        />
      )}
    </div>
  );
}
